import { intercept } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { withRouter } from 'react-router';
import { Input } from 'reactstrap';
import { ErrorsStore } from 'src/Stores/Errors/Errors';
import { WellsStore } from 'src/Stores/Wells/WellsStore';
import { decimalPlaces } from '../../UtilityFunctions/DecimalPlaces';
import EInputMask from './EInputMask';
import EInputValidation from './EInputValidation';


/**
 * 
 * A Custom component used to output Input elements, which accept attributes
 * for client-side validation, types, and masks. It also uses its own MobX
 * store for display field level API errors
 * 
 * @param required - [boolean] ('true'|'false') - sets the field as required and displays error if value is empty
 * @param dataValidation - [string] ('string' | 'integer' | 'email' | 'phone') - preset validation of specific data types
 * @param dataMin - [integer] - minimum value allowed
 * @param dataMax - [integer] - maximum value allowed
 * @param dataMask - [string] ('phone' | 'currency') - applies an input mask to the data being entered by the user
 * 
 */

@inject('stores')
@observer
class EInput extends React.Component {
    private validation = new EInputValidation();
    private mask = new EInputMask();
    public props: any;
    private errorDisposer: any;
    private hasError: boolean = false;
    public ErrorStore: ErrorsStore;
    public WellsStore: WellsStore;
    public errorMessages: any[] = [];
    private inputRef: any = React.createRef();
    private guid: any;
    public timeout: any = undefined;

    public validationErrors: object = {};

    public state = {
        componentOnChange: (event: any) => { return; },
        errorMessages: this.errorMessages,
        focused: false,
        hasError: this.hasError,
        refValue: '',
        validationErrors: this.validationErrors
    };

    constructor(props: any) {
        super(props);

        this.state = {
            componentOnChange: (event: any) => { return; },
            errorMessages: this.errorMessages,
            focused: false,
            hasError: this.hasError,
            refValue: '',
            validationErrors: this.validationErrors
        };
        
        this.ErrorStore = this.props.stores.ErrorsStore;
        this.WellsStore = props.stores.WellsStore;
        this.guid = this.ErrorStore.guid();

        this.noValidationErrors = this.noValidationErrors.bind(this);
        this.checkForErrors = this.checkForErrors.bind(this);
        this.checkProperties = this.checkProperties.bind(this);
        this.checkForRequired = this.checkForRequired.bind(this);
        this.createDisposers = this.createDisposers.bind(this);
        this.clearDisposers = this.clearDisposers.bind(this);
        this.addRemoveValidationError = this.addRemoveValidationError.bind(this);
        this.removeApiErrors = this.removeApiErrors.bind(this);
        this.handleOnChange = this.handleOnChange.bind(this);
        this.handleOnBlur = this.handleOnBlur.bind(this);
    }

    public componentOnChange = (event: any) => event;

    private noValidationErrors() {
        const messes = this.state.validationErrors;
        for (const key in messes) {
            if (messes.hasOwnProperty(key)) {
                return false;
            }
        }
        
        return true;
    }

    public componentWillMount() {
        let refValue = this.props.value;

        if ( this.props.hasOwnProperty('multiple') && this.props.multiple ) {
            refValue = this.props.value || [];
        }
        
        this.setState({
            refValue
        });
    }

    public componentDidMount() {
        let value = this.props.value;

        if ( this.props.hasOwnProperty('multiple') && this.props.multiple ) {
            value = this.props.value || [];
        }

        this.componentOnChange = this.props.onChange;
        
        this.setState({
            // componentOnChange: this.props.onChange,
            refValue: value
        });

        this.createDisposers();
        this.checkForErrors();
        this.checkProperties();
        this.checkForRequired();
        this.checkForMask();
    }

    public componentDidUpdate(prevProps: any, prevState: any, snapshot: any) {
        const a: any = ''+prevProps.value;
        const b: any = ''+this.props.value;
        if ( a !== b ) {
            this.checkForErrors();
            this.checkProperties();
            this.checkForRequired();
            this.checkForMask();
        }
    }
    
    public componentWillUpdate(nextProps: any) {
        // converts values to string so we can compare values for values
        const a: any = ''+nextProps.value;
        const b: any = ''+this.props.value;
        if ( a !== b ) {
            this.componentOnChange = nextProps.onChange;

            this.setState({
                refValue: nextProps.value
            });

            this.checkForRequired(nextProps.value);
            this.checkForMask(undefined, nextProps.value);
        }
    }

    public componentWillUnmount() {
        this.ErrorStore.removeFromErrors(this.guid);
        this.clearDisposers();
    }

    public createDisposers() {
        this.errorDisposer = intercept(this.ErrorStore, 'errors', (change: any) => {
            const errors = change.newValue;
            
            if ( errors.hasOwnProperty(this.props.name) ) {
                this.state.hasError = true;

                this.state.errorMessages = errors[this.props.name];
            }

            this.checkProperties();

            return change;
        });
    }

    public clearDisposers() {
        this.errorDisposer();
    }

    private checkForErrors() {
        if ( this.ErrorStore.errors.hasOwnProperty(this.props.name) ) {
            this.state.hasError = true;
        }
    }

    // TODO: do not add another instance of the error, if it already there
    private checkProperties(value?: any) {
        const props = this.props;
        const val = (typeof value === undefined || value === undefined) ? this.props.value : value;

        if ( props['data-validation'] ) {
            if ( this.validation[props['data-validation']] ) {
                const type = props['data-validation'];
                const result = this.validation[props['data-validation']](val);
                this.addRemoveValidationError(type, result);
            }
        }

        if ( props['data-min'] !== undefined || props['data-max'] !== undefined ) {
            const result = this.validation.range(val, props['data-min'], props['data-max']);
            this.addRemoveValidationError('range', result);
        }

        if ( props['decimal-places'] !== undefined ) {
            if (Number.isFinite(Number(props['decimal-places'])) && Number.isFinite(Number(val))) {
                const decimal = Number(props['decimal-places']);
                const numVal = Number(val);
                let result: boolean | object = true;
                if (decimalPlaces(numVal) <= decimal) {
                    result = true;
                } else {
                    result = {'message': `Value has a maximum of ${decimal} decimal places.` };
                }
                this.addRemoveValidationError('decimalplaces', result);
            }
        }
    }

    private checkForRequired(value?: any) {
        const props = this.props;
        const val = (typeof value === undefined || value === undefined) ? props.value : value;

        if ( props.hasOwnProperty('required') && props.required ) {
            const result = this.validation.validateRequired(val);
            this.addRemoveValidationError('required', result);
        }
    }

    private checkForMask(event?: any, value?: any) {
        if ( this.props['data-mask']) {
            const maskType = this.props['data-mask'];
            
            if ( this.mask[maskType] ) {
                if (typeof event !== undefined && event !== undefined ) {
                    this.setState({
                        refValue: this.mask[maskType](event.target.value)
                    });

                } else {
                    const val = (typeof value === undefined || value === undefined) ? this.props.value : value;
                    this.setState({
                        refValue: this.mask[maskType](val, event)
                    });
                }
            }

            return true;
        }

        return false;
    }

    private addRemoveValidationError(type: any, result: any) {
        if ( result instanceof Object && result.hasOwnProperty('message') ) {
            const validationErrors = this.state.validationErrors;
            validationErrors[type] = result;
            
            this.ErrorStore.addToErrors(this.guid, type, result);

            this.setState({
                hasError: true,
                validationErrors
            });

        } else {
            const validationErrors = this.state.validationErrors;
            let hasError = false;

            delete validationErrors[type];

            if ( !this.ErrorStore.isEmpty(this.state.errorMessages) || !this.ErrorStore.isEmpty(this.state.validationErrors) ) {
                hasError = true;
            }

            this.ErrorStore.removeFromErrors(this.guid, type);

            this.setState({
                hasError,
                validationErrors
            });

        }

    }

    public handleOnFocus = (event: any) => {
        event.persist();
        this.setState({
            focused: true,
        }, () => {
            const mask = this.props['data-mask'];
            if (mask && mask === 'currency') {
                event.target.select();
            }
        });
    }
    private removeApiErrors() {

        this.setState({
            errorMessages: []
        });

        this.ErrorStore.clearError(this.props.name);

        if ( this.noValidationErrors() ) {
            this.setState({
                hasError: false
            });
        }
    }

    // private returnClassName() {
    //     if ( typeof this.props.disabled !== undefined && this.props.disabled ) {
    //         return 'disabled-input-plain';
    //     }

    //     return '';
    // }

    public handleOnChange(event: any) {
        event.persist();


        if ( this.props.onChange ) {
            const mask = this.props['data-mask'];
            if (mask && this.mask[mask] && mask === 'phone') {
                event.target.value = this.mask[mask](event.target.value);
                this.props.onChange(event);
            }
            this.props.onChange(event);
        }

        this.removeApiErrors();

        this.checkProperties(event.target.value);


        if ( !this.checkForMask(event) ) {
            this.setState({
                refValue: event.target.value
            });
        }
    }

    public handleOnBlur(event: any) {
        this.setState({
            focused: false,
        });
    }

    private errorCallback = () => {
        return this.props.errorCallback && this.props.errorCallback.callback && this.props.errorCallback.callback() && this.props.errorCallback.message !== undefined;
    }

    private errorCallbackArray = (index?: number) => {
        if (index === undefined) {
            return this.props.errorCallback && Array.isArray(this.props.errorCallback) && this.props.errorCallback.some((ec: any) => ec.callback());
        }
        return this.props.errorCallback 
            && this.props.errorCallback[index]
            && this.props.errorCallback[index].callback
            && this.props.errorCallback[index].message !== undefined
            && this.props.errorCallback[index].callback();
    }

    public returnInputElement() {
        const { errorCallback, disabled, ...inputProps } = this.props;
        const currWell = this.WellsStore.well;
        const disableWellStatus = currWell !== undefined && currWell.wellStatus !== 'Active' && !this.props.location.pathname.includes('wellclose');
        return(
            !this.props.children ? (
                <Input {...inputProps} 
                    value={this.state.focused ? this.props.value : this.state.refValue} 
                    ref={this.inputRef} 
                    onChange={this.handleOnChange} 
                    onBlur={this.handleOnBlur}
                    onFocus={this.handleOnFocus}
                    disabled={disabled || disableWellStatus}
                    autocomplete="new-password"
                />
             ) : (
                 <Input {...inputProps} value={this.state.refValue} ref={this.inputRef} onChange={this.handleOnChange} disabled={disabled || disableWellStatus} autocomplete="new-password" >
                     {this.props.children}
                 </Input>
             )
        );
    }

    public render() {
        return( 
            <div key={this.props.key} className={((this.state.hasError || this.errorCallback() || this.errorCallbackArray()) ? 'einput-wrapper input-error' : 'einput-wrapper')}>
                {this.returnInputElement()}
                { this.props.label && <span style={{marginLeft: "10px"}}>{this.props.label}</span>}
                {((this.state.hasError && Object.keys(this.state.validationErrors).filter(type => this.state.validationErrors[type].message !== '').length > 0)
                    || this.errorCallback()
                    || this.errorCallbackArray()
                    || this.state.errorMessages.length > 0) &&
                    <div className="error-messages">
                        {this.state.errorMessages.map((message: any, index: number) => 
                            <p className="errorMessages" key={index}>{message}</p>
                        )}
                        {Object.keys(this.state.validationErrors).filter(type => this.state.validationErrors[type].message !== '').map((type: any, index: number) => 
                            <p className="validationError" key={index}>{this.state.validationErrors[type].message}</p>
                        )}
                        {
                            this.errorCallback() &&
                            <p className="validationError">{this.props.errorCallback.message}</p>
                        }
                        {
                            this.errorCallbackArray() &&
                            this.props.errorCallback.map((value : any, index: number) => 
                            this.errorCallbackArray(index) &&
                            <p className="validationError">{value.message}</p>
                            )
                        }
                    </div>
                }
            </div>
        );
    }
}

export default withRouter(EInput);


// WEBPACK FOOTER //
// ./src/Components/EInput/EInput.tsx